<template>
  <Breadcrumbs title="" />
  <div class="container-fluid coupon-order-page">
    <div class="row">
      <div class="col-sm-12 col-md-8 offset-md-2">
        <div class="card premium-card">
          <div class="card-header premium-header">
            <div v-if="loading" class="text-center" style="padding: 24px 0;">
              <div class="spinner-border text-primary" role="status" aria-label="Loading order"></div>
              <div style="margin-top: 10px;">{{ t('loading') }}</div>
            </div>

            <div v-else>
              <div class="order-cover-wrap" v-if="orderCoverUrl">
                <img :src="orderCoverUrl" alt="Event cover" class="order-cover-image" />
              </div>

              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 order-top">
                <div>
                  <h5 class="order-title">{{ t('page_title') }}</h5>
                  <div class="order-uuid">Order No: {{ order.uuid }}</div>
                </div>
              </div>

              <div class="payment-status" :class="order.status_order === 'PAID' ? 'paid' : 'initial'">
                {{ order.status_order === 'PAID' ? t('paid') : t('waiting_payment') }}
              </div>

              <div class="coupon-hero" v-if="order.status_order === 'PAID' && order.coupon_code">
                <div class="coupon-hero-label">{{ t('coupon_code') }}</div>
                <div class="coupon-hero-code">{{ order.coupon_code }}</div>
                <div class="coupon-copy-status" v-if="copyStatus">
                  {{ copyStatus }}
                </div>
                <div class="coupon-copy-row">
                  <button class="btn btn-link coupon-copy-btn" @click="copyCouponCode">
                    <vue-feather type="copy" size="12"></vue-feather>
                    {{ t('copy_coupon') }}
                  </button>
                </div>
              </div>

              <div class="qr-wrap" v-if="order.status_order !== 'PAID' && order.qrcode_url">
                <div v-if="!qrImageLoaded" class="qr-placeholder">
                  <div class="spinner-border text-primary" role="status" aria-label="Loading QR code"></div>
                  <div class="qr-placeholder-text">Loading QR code...</div>
                </div>
                <img
                  :src="order.qrcode_url"
                  alt="Payment QR Code"
                  class="qr-image"
                  @load="qrImageLoaded = true"
                  @error="qrImageLoaded = true"
                  v-show="qrImageLoaded"
                />
              </div>

              <p v-if="order.status_order !== 'PAID'" style="margin-top: 10px; color: #6c757d; text-align: center;">
                {{ t('refresh_note') }}
              </p>

              <div class="d-flex justify-content-center" v-if="order.status_order !== 'PAID'">
                <button class="btn btn-outline-primary btn-sm premium-refresh-btn" @click="refreshOrder" :disabled="loading">
                  <vue-feather type="refresh-cw" size="16"></vue-feather>
                  {{ t('refresh') }}
                </button>
              </div>

              <div class="usage-note" v-if="order.status_order !== 'PAID'">
                <p style="margin-bottom: 6px;">
                  {{ t('coupon_paid_note') }}
                </p>
                <p style="margin-bottom: 6px;">
                  {{ t('mobile_qr_note') }}
                </p>
                <p style="margin-bottom: 0;">
                  {{ t('contact_note') }}
                  <a :href="fbUrl" target="_blank"><b><u>{{ fbName }}</u></b></a>
                </p>
              </div>

              <div class="payment-summary">
                <div class="summary-row">
                  <span>{{ t('coupon_price_amount') }}</span>
                  <b>{{ formatCurrency(order.amount) }} {{ order.payment_currency || 'THB' }}</b>
                </div>
                <div class="summary-row" v-if="getPaymentFee() > 0">
                  <span>{{ t('payment_fee') }}</span>
                  <b>{{ formatCurrency(getPaymentFee()) }} {{ order.payment_currency || 'THB' }}</b>
                </div>
                <div class="summary-row total">
                  <span>{{ t('paid_amount_total') }}</span>
                  <b>{{ formatCurrency(order.paid_amount) }} {{ order.payment_currency || 'THB' }}</b>
                </div>
              </div>

              <div class="detail-box">
                <div><b>OrderNo:</b> {{ order.uuid }}</div>
                <div><b>{{ t('name') }}:</b> {{ order.name }}</div>
                <div><b>{{ t('mobile') }}:</b> {{ order.mobile }}</div>
                <div><b>{{ t('email') }}:</b> {{ order.email ? order.email : '-' }}</div>
                <div><b>{{ t('create_date') }}:</b> {{ order.create_date || '-' }}</div>
                <div v-if="order.payment_date"><b>{{ t('payment_date') }}:</b> {{ order.payment_date }}</div>
                <div v-if="order.status_order === 'PAID' && order.coupon_code">
                  <b>{{ t('coupon_code') }}:</b>
                  <span class="coupon-code">{{ order.coupon_code }}</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import router from '../../router';

export default {
  data() {
    return {
      currentLanguage: 'th',
      fbName: process.env.VUE_APP_SYSTEM_NAME || '',
      fbUrl: process.env.VUE_APP_SYSTEM_FACEBOOK_URL || '',
      s3BaseUrl: process.env.VUE_APP_AWS_S3_URL || '',
      loading: false,
      qrImageLoaded: false,
      copyStatus: '',
      order: {
        uuid: '',
        event_key: '',
        qrcode_url: '',
        amount: 0,
        paid_amount: 0,
        payment_currency: 'THB',
        status_order: 'INITIAL',
        payment_date: '',
        coupon_code: '',
        create_date: '',
        name: '',
        mobile: '',
        email: '',
      },
      dict: {
        page_title: { en: 'Coupon order detail', th: 'รายละเอียดคำสั่งซื้อคูปอง' },
        loading: { en: 'Loading...', th: 'กำลังโหลดข้อมูล...' },
        refresh: { en: 'Refresh', th: 'รีเฟรช' },
        paid: { en: 'Payment completed', th: 'ชำระเงินเรียบร้อย' },
        waiting_payment: { en: 'Waiting for payment', th: 'รอการชำระเงิน' },
        coupon_price_amount: { en: 'Coupon price', th: 'ราคาคูปอง' },
        payment_fee: { en: 'Payment fee', th: 'ค่าธรรมเนียมการชำระเงิน' },
        paid_amount_total: { en: 'Total to pay', th: 'รวมยอด' },
        name: { en: 'Name', th: 'ชื่อ' },
        mobile: { en: 'Mobile number', th: 'หมายเลขโทรศัพท์' },
        email: { en: 'Email', th: 'อีเมล' },
        create_date: { en: 'Create date', th: 'วันที่สร้างรายการ' },
        payment_date: { en: 'Payment date', th: 'วันที่ชำระเงิน' },
        coupon_code: { en: 'Coupon code', th: 'รหัสคูปอง' },
        copy_coupon: { en: 'Copy coupon code', th: 'คัดลอกรหัสคูปอง' },
        copied_success: { en: 'Copied coupon code successfully', th: 'คัดลอกรหัสคูปองเรียบร้อยแล้ว' },
        copied_failed: { en: 'Unable to copy, please copy manually', th: 'ไม่สามารถคัดลอกอัตโนมัติได้ กรุณาคัดลอกด้วยตนเอง' },
        refresh_note: { en: 'Please refresh after payment is completed.', th: 'กรุณารีเฟรชหลังชำระเงินเรียบร้อย' },
        coupon_paid_note: { en: 'The coupon details will appear after payment is completed.', th: 'คูปองจะแสดงเมื่อชำระเงินเรียบร้อยแล้ว' },
        mobile_qr_note: { en: 'If you use a mobile phone, you can take a screenshot and scan the QR code to pay with PromptPay.', th: 'หากใช้งานบนอุปกรณ์โทรศัพท์มือถือ สามารถแคปหน้าจอ เพื่อเอา QRCode ไปสแกนจ่ายพร้อมเพย์ได้เลย' },
        contact_note: { en: 'If you have any problems, please contact us at: ', th: 'ติดปัญหาในการใช้งานสามารถสอบถามข้อมูลเพิ่มเติมได้ที่เพจ: ' },
      },
    };
  },
  computed: {
    orderCoverUrl() {
      const base = (this.s3BaseUrl || '').replace(/\/+$/, '');
      const eventKey = (this.order?.event_key || '').trim();
      if (!base || !eventKey) {
        return '';
      }
      return `${base}/${eventKey}/cover.jpg`;
    },
  },
  async created() {
    if (localStorage.getItem('currentLanguage') !== null) {
      this.currentLanguage = localStorage.getItem('currentLanguage');
    }
  },
  async mounted() {
    await this.loadOrder();
  },
  methods: {
    t(key) {
      const item = this.dict[key];
      return this.currentLanguage === 'en' ? item.en : item.th;
    },
    toNumber(value) {
      const normalized = typeof value === 'string' ? value.replace(/,/g, '') : value;
      const n = Number(normalized);
      return Number.isFinite(n) ? n : 0;
    },
    formatCurrency(value) {
      const n = this.toNumber(value);
      return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getPaymentFee() {
      const paid = this.toNumber(this.order?.paid_amount);
      const amount = this.toNumber(this.order?.amount);
      const fee = paid - amount;
      return fee > 0 ? fee : 0;
    },
    async copyCouponCode() {
      const code = this.order?.coupon_code;
      if (!code) {
        return;
      }
      try {
        await navigator.clipboard.writeText(code);
        this.copyStatus = this.t('copied_success');
      } catch (error) {
        this.copyStatus = this.t('copied_failed');
      }
      setTimeout(() => {
        this.copyStatus = '';
      }, 2000);
    },
    async loadOrder() {
      this.loading = true;
      this.qrImageLoaded = false;
      this.copyStatus = '';
      try {
        const res = await axios.get(`/v1/coupon/q/${this.$route.params.uuid}`);
        if (res.data.status === 'success') {
          this.order = res.data;
          this.qrImageLoaded = false;
        } else {
          router.push({ path: '/404' });
        }
      } catch (error) {
        router.push({ path: '/404' });
      }
      this.loading = false;
    },
    async refreshOrder() {
      await this.loadOrder();
    },
  },
};
</script>

<style scoped>
.coupon-order-page {
  background: radial-gradient(circle at 10% 10%, #f4f8ff 0%, #ffffff 45%, #f3f6fb 100%);
  padding-top: 6px;
  padding-bottom: 18px;
}

.premium-card {
  border: 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(11, 39, 76, 0.12);
}

.premium-header {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
  padding: 18px 18px 20px;
}

.order-cover-wrap {
  margin-bottom: 12px;
}

.order-cover-image {
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  display: block;
}

.order-top {
  padding-bottom: 10px;
  border-bottom: 1px solid #edf1f7;
}

.order-title {
  margin-bottom: 4px;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #102a43;
}

.order-uuid {
  font-size: 0.84rem;
  color: #627d98;
}

.payment-status {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 800;
  margin-top: 14px;
  font-size: 0.9rem;
}

.payment-status.initial {
  background: linear-gradient(135deg, #fff7dd, #ffefb3);
  color: #8a6110;
  border: 1px solid #ffe29a;
}

.payment-status.paid {
  background: linear-gradient(135deg, #e5fbef, #caf7de);
  color: #14532d;
  border: 1px solid #98ecbf;
}

.coupon-hero {
  margin-top: 14px;
  margin-bottom: 8px;
  padding: 16px 14px;
  border-radius: 14px;
  border: 1px solid #a9e8c5;
  background: linear-gradient(135deg, #e9fff3, #dff9eb 60%, #f4fffa 100%);
  text-align: center;
  box-shadow: 0 10px 22px rgba(25, 135, 84, 0.16);
}

.coupon-hero-label {
  font-size: 0.84rem;
  color: #2b6e49;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  font-weight: 700;
}

.coupon-hero-code {
  font-size: 1.55rem;
  letter-spacing: 1.3px;
  font-weight: 900;
  color: #0f5132;
  background: #fff;
  border: 1px dashed #3b9b68;
  border-radius: 10px;
  padding: 10px 12px;
  margin: 0 auto 10px;
  display: inline-block;
  min-width: 220px;
}

.premium-refresh-btn {
  margin-top: 8px;
  border-radius: 999px;
  padding: 6px 14px;
  font-weight: 700;
}

.coupon-copy-status {
  margin-top: 4px;
  font-size: 0.85rem;
  color: #198754;
  font-weight: 600;
}

.coupon-copy-row {
  margin-top: 2px;
  display: flex;
  justify-content: center;
}

.coupon-copy-btn {
  padding: 2px 6px;
  font-size: 0.76rem;
  font-weight: 600;
  color: #2f855a;
  text-decoration: none;
  line-height: 1.1;
}

.coupon-copy-btn:hover,
.coupon-copy-btn:focus {
  color: #1f6f48;
  text-decoration: underline;
}

.qr-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.qr-image {
  width: 250px;
  max-width: 100%;
  height: auto;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1);
}

.qr-placeholder {
  width: 250px;
  max-width: 100%;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
}

.qr-placeholder-text {
  font-size: 0.9rem;
  color: #6c757d;
}

.payment-summary {
  margin-top: 14px;
  border: 1px solid #e6edf5;
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #f5f8fc;
  text-align: left;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.96rem;
  margin-bottom: 6px;
}

.summary-row.total {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #cfd8e3;
}

.usage-note {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7f9fc 0%, #f2f5f9 100%);
  border: 1px solid #e2e8f0;
  color: #334155;
  text-align: left;
  font-size: 0.92rem;
  line-height: 1.5;
}

.detail-box {
  margin-top: 14px;
  text-align: left;
  border: 1px solid #e6edf5;
  border-radius: 12px;
  padding: 13px 14px;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #f5f8fc;
  line-height: 1.7;
}

.coupon-code {
  display: inline-block;
  margin-left: 6px;
  padding: 3px 10px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px dashed #8fa1b5;
  font-weight: 800;
  letter-spacing: 0.7px;
}

@media (max-width: 576px) {
  .premium-header {
    padding: 14px 12px 16px;
  }

  .order-title {
    font-size: 1.08rem;
  }

  .coupon-hero-code {
    min-width: 100%;
    font-size: 1.3rem;
  }
}
</style>